// Collections: the one money screen. Charges, payments and the state an invoice is in.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const money = {
  collections: {
    // WHAT SURVIVES OF THE OLD גבייה SCREEN: five column labels, kept because the
    // DASHBOARD's overdue and unpaid-expense tables still use them. Everything else that
    // screen owned — its title, its "open balances only" note, its status and
    // record-payment strings — went with it, along with `empty.collections`.
    //
    // They stay under `collections` rather than moving to `dashboard` because they are
    // the vocabulary of a charge (client, job, issuer, balance, due date), and the money
    // screen's own strings live in `board` below under the same roof.
    client: 'לקוח',
    job: 'עבודה',
    issuer: 'מפיק/ה',
    balance: 'יתרה',
    dueDate: 'תאריך יעד',

    // THE ONE MONEY SCREEN. Two questions, and the wording must keep them apart:
    // what has been billed and is still owed (כסף לגבייה), and what has not been billed
    // yet and should be (חיוב שצריך להוציא). An expected row is NEVER called "לגבייה" —
    // nobody owes it until an invoice exists, and a screen that blurs the two teaches
    // you to distrust all of it.
    board: {
      title: 'כספים — גבייה וחיובים',
      kindRetainer: 'ריטיינר',
      kindOneOff: 'פרויקט חד־פעמי',
      kindBrokerage: 'תיווך',
      month: 'חודש',
      amount: 'סכום',
      collected: 'נגבה',
      balance: 'יתרה',
      dueDate: 'תאריך יעד',
      date: 'תאריך',
      // THE STATE OF A DEBT, NOT OF A DOCUMENT. This screen has no "issue an invoice"
      // concept — the real invoice is produced in the accounting software — so no row
      // here may say "הופקה". Every state below is derived from money alone: what is
      // owed, what came in, and whether the due date has passed.
      stateCollected: 'נגבה במלואו',
      stateOverdue: 'באיחור',
      statePartial: 'נגבה חלקית',
      stateOpen: 'פתוח לגבייה',
      overdue: 'באיחור',
      toBill: 'לחיוב',
      skipped: 'לא מחויב',
      future: 'טרם הגיע',
      outOfTerm: 'מחוץ לתקופת החוזה',
      outstanding: 'פתוח לגבייה',
      monthly: 'סכום חודשי',
      months: (n) => `${n} חודשים`,
      contractValue: 'שווי החוזה',
      openEnded: 'ללא תאריך סיום',
      invoiced: 'חויב',
      collectedTotal: 'נגבה',
      agreed: 'סכום מוסכם',
      cost: 'עלות',
      recordInvoice: 'רישום חשבונית',
      recordPayment: 'רישום תשלום',
      // "HAS THE CLIENT PAID?" on the recording dialog — no default; see PaidNowFields.
      paidQuestion: 'האם הלקוח כבר שילם?',
      paidYes: 'כן, שולם',
      paidNo: 'עדיין לא',
      paidRequired: 'יש לבחור אם הלקוח כבר שילם.',
      recordCharge: 'רישום חיוב',
      addCharge: 'רישום חיוב נוסף',
      toRaise: 'חיובים שצריך להוציא',
      skip: 'לא לחייב החודש',
      unskip: 'החזרה לחיוב',
      showSettled: (n) => `הצג ${n} שסגורים`,
      noMonths: 'אין חודשי חיוב להצגה.',
      empty: 'אין כספים לגבייה ואין חיובים שממתינים.',
      // The two groups, named as the rest of the system names them — the same hierarchy
      // the client card presents, so the money screen reads with one mental model.
      retainersHeading: 'ריטיינרים',
      // What the retainer and job screens say now that they only SHOW the history. Both
      // name where the writing happens, so a partner looking for the button is not left
      // hunting — the affordance moved, it did not disappear.
      goToMoney: 'למסך הכספים',
      // RECORDING A CHARGE. The system does not issue invoices — the real document is
      // produced in the accounting software — so the wording says "רישום", never "הפקה".
      recordChargeTitle: 'רישום חיוב',
      recordChargeConfirm: 'רישום החיוב',
      forMonth: 'עבור חודש',
      invoiceDate: 'תאריך החשבונית',
      issuedBy: 'מי חייב/ה',
      category: 'סוג ההכנסה',
      noCategory: 'ללא סיווג',
      description: 'תיאור',
      chargingCost: 'החיוב מכסה עלות של',
      invoiceFile: 'צילום / סריקה של החשבונית',
      attachInvoiceFile: 'צירוף צילום החשבונית',
      attachAnother: 'צירוף קובץ נוסף',
      // Names the FILE and says the charge survives — the one thing to be sure of before
      // deleting anything on a money screen. The R2 object goes with the row.
      confirmDeleteFile: (name) =>
        `למחוק את "${name}"? הקובץ יימחק לצמיתות מהאחסון. החיוב עצמו, הסכום והתשלומים שנרשמו — נשארים.`,
      // THE CONVERSION, STATED BEFORE IT HAPPENS, with the real figures. Every number
      // below is correct after the change; that is exactly why it must be said — nothing
      // looks broken, the figures simply move, and whoever was not told will find them
      // later and not know why.
      //
      // IT SAYS "מספרים" AND NOT "שני מספרים" because it is no longer two: the profit
      // move and the SPLIT move are different kinds of change — one is a report, the
      // other is the standing gap between the partners — and counting them in the title
      // invites the reader to stop after the second bullet.
      conversionTitle: 'שימו לב — רישום החיוב הזה משנה מספרים אמיתיים:',
      conversionBecomesBilling: (job) => `העבודה "${job}" תהפוך לעבודה שמחייבת בעצמה.`,
      conversionCostMoves: (retainer) => `עלויות העבודה יפסיקו להתגלגל לרווח של "${retainer}":`,
      conversionRetainerProfit: 'רווח הריטיינר',
      conversionJobProfit: 'העבודה תדווח',
      // THE SPLIT MOVE — the gap's half, and the reason the old single sentence had to
      // go. It promised "50/50", which is only true when both partners' default shares
      // happen to be 50: the conversion resolves the split from default_share_pct. These
      // print the two REAL splits the server resolved, side by side.
      conversionSplitToday: (retainer) => `היום עלויות העבודה מתחלקות לפי החלוקה של "${retainer}":`,
      conversionSplitAfter: 'מעכשיו ההכנסה והעלויות של העבודה יתחלקו לפי חלוקה משלה:',
      shareLine: (name, pct) => `${name} ${pct}%`,
      conversionSplitSame: 'החלוקה זהה לזו של הריטיינר, ולכן הפער בין השותפים אינו זז — רק הרווח המדווח.',
      conversionSplitDiffers: 'החלוקות שונות, ולכן גם הפער בין השותפים יזוז. ניתן לערוך את החלוקה במסך העבודה.',
      conversionSplitUnavailable: 'לא ניתן לקבוע חלוקת רווח לעבודה. הרישום ייכשל עד שתוגדר חלוקה תקינה לשותפים הפעילים.',
      // THE GATE. This dialog now opens on every brokerage cost recorded against a topic
      // inside a retainer, and a warning that appears every time is one people learn to
      // click past. Save does not work until this is ticked.
      conversionAck: 'קראתי והבנתי מה הרישום הזה משנה',
      // CORRECTING A RECORDED CHARGE — its amount, its date, who issued it. Every line
      // below says what the change MOVES, because two of the three move money.
      correct: 'תיקון',
      correctTitle: 'תיקון חיוב',
      correctConfirm: 'שמירת התיקון',
      correctAmountFloor: (paid) => `כבר נגבו ${paid} על החיוב הזה, והסכום לא יכול לרדת מתחת לזה.`,
      // The one correction that moves money between the partners: collected money is
      // credited to whoever issued the invoice, so naming another issuer moves it.

      // CANCELLING A CHARGE RECORDED BY MISTAKE — only while nothing has been paid, and
      // never a deletion: the record stays, as a cancelled charge.
      cancelCharge: 'ביטול',
      cancelTitle: 'ביטול חיוב',
      cancelConfirm: 'ביטול החיוב',
      cancelLead: (amount) => `החיוב על סך ${amount} יוסר ממסך הכספים ולא ייספר עוד בהכנסות וברווח.`,
      // Cancelling a month's charge is not billing that month (068): it is marked
      // "לא מחויב", and "החזרה לחיוב" makes it owed again.
      cancelMonthBack: 'החודש יסומן כ"לא מחויב". "החזרה לחיוב" תחזיר אותו לחיוב.',
      cancelCostsBack: (n) => (n === 1
        ? 'ההוצאה שחויבה בו תחזור לרשימת החיובים להוצאה, במחיר שתוכנן לה.'
        : `${n} ההוצאות שחויבו בו יחזרו לרשימת החיובים להוצאה, במחיר שתוכנן להן.`),
      cancelJobStaysBilling: 'העבודה תישאר עבודה שמחייבת בעצמה.',
      cancelCardLeaves: 'זה החיוב היחיד של העבודה ואין בה חיובים ממתינים, ולכן היא תרד ממסך הכספים.',
      // UNDOING A PAYMENT RECORDED BY MISTAKE. The gap's own vocabulary — "לזכות" /
      // "לחובה", as on the settlement screen — and never "חוב", "לשלם" or "להעביר": the
      // partners never transfer money, and the gap is a standing figure nothing pays down.
      payments: 'תשלומים',
      paymentsTitle: 'התשלומים שנרשמו על החיוב',
      paymentsNone: 'לא נרשמו תשלומים על החיוב.',
      undoPayment: 'ביטול תשלום',
      undoTitle: 'ביטול תשלום שנרשם בטעות',
      undoConfirm: 'ביטול התשלום',
      undoBack: 'חזרה לרשימת התשלומים',
      undoLead: (amount) => `התשלום על סך ${amount} יימחק לגמרי, כאילו לא נרשם.`,
      undoGapLead: 'הפער המצטבר בין השותפים ישתנה כך:',
      undoGapCredit: (name, amount) => `${name}: ${amount} לזכות`,
      undoGapDebit: (name, amount) => `${name}: ${amount} לחובה`,
      // "NOT BILLING THIS MONTH" — a decision recorded, never a deletion.
      skipTitle: 'סימון חודש שלא יחויב',
      skipConfirm: 'סימון',
      skipReason: 'סיבה (לא חובה)',
    },
  },
  payment: {
    title: 'רישום תשלום',
    amount: 'סכום',
    date: 'תאריך',
    method: 'אמצעי',
    notes: 'הערות',
    // WHO COLLECTED IT is who issued the invoice — the balance between the partners
    // credits collected money to the issuer — so it can change only until the first payment.
    collectedBy: 'מי גבה',

    file: 'צילום / סריקה של החשבונית',
    fileFailed: 'התשלום נשמר, אך העלאת הקובץ נכשלה. אפשר לצרף אותו שוב מהחיוב במסך הכספים.',
  },
  invoiceStatus: {
    draft: 'טיוטה',
    issued: 'הופקה',
    partially_paid: 'שולם חלקית',
    paid: 'שולם',
    cancelled: 'בוטלה',
  },
};

export default money;
